"use client";

import { useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart } from "@fortawesome/pro-solid-svg-icons/faHeart";
import { faBolt } from "@fortawesome/pro-solid-svg-icons/faBolt";
import { cn, Button } from "@wondermaps/ui";
import { useTranslation } from "react-i18next";
import { findIconByName } from "../../data/fontawesome-icons";
import { contrastText } from "../../lib/color-utils";

export interface CategoryForCarousel {
  slug: string;
  name: string;
  nameTranslations?: Record<string, string> | null;
  icon: string;
  color: string;
  sortOrder: number;
  isVisible?: boolean;
  isFilterable?: boolean;
}

interface CategoryCarouselProps {
  categories: CategoryForCarousel[];
  selectedCategory: string; // 'all', 'saved', 'happening-now', or category slug
  onSelectCategory: (slug: string) => void;
  poiCounts: Record<string, number>; // categorySlug -> count
  savedCount?: number; // Number of bookmarked POIs
  happeningNowCount?: number; // Number of POIs with events happening now
  className?: string;
  brandAccent?: string | null;
}

export function CategoryCarousel({
  categories,
  selectedCategory,
  onSelectCategory,
  poiCounts,
  savedCount = 0,
  happeningNowCount = 0,
  className,
  brandAccent,
}: CategoryCarouselProps) {
  const { t: tMap } = useTranslation("map");
  const { t: tPois, i18n } = useTranslation("pois");

  const selectedRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ inline: "center", behavior: "smooth", block: "nearest" });
  }, [selectedCategory]);

  // Filter out categories with 0 POIs and sort by sortOrder
  const visibleCategories = categories
    .filter((cat) => {
      const count = poiCounts[cat.slug] ?? 0;
      return count > 0;
    })
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // Calculate total POI count for "All" chip
  const totalPois = Object.values(poiCounts).reduce((sum, c) => sum + c, 0);

  return (
    <div
      role="tablist"
      aria-label={tMap("category.filterByCategory")}
      className={cn(
        // Container with frosted glass background for floating above map
        "bg-background/95 backdrop-blur-sm shadow-md",
        // Horizontal scroll with hidden scrollbar
        "flex gap-2 overflow-x-scroll py-3 px-4",
        // Hide scrollbar across all browsers
        "[&::-webkit-scrollbar]:!hidden [&::-webkit-scrollbar]:!w-0 [&::-webkit-scrollbar]:!h-0",
        "[-ms-overflow-style:none] [scrollbar-width:none]",
        className
      )}
    >
      {/* "All" chip - no icon, min-width ensures ~3.5 chips visible on mobile */}
      <Button
        ref={selectedCategory === "all" ? selectedRef : undefined}
        role="tab"
        aria-selected={selectedCategory === "all"}
        aria-label={tMap("category.showAll", { count: totalPois })}
        variant={selectedCategory === "all" ? "default" : "outline"}
        size="sm"
        className={cn(
          "shrink-0 h-10 min-w-[72px] px-4 rounded-full",
          "text-sm font-medium whitespace-nowrap",
          selectedCategory === "all" && !brandAccent && "font-semibold bg-foreground text-background hover:bg-foreground/90",
          selectedCategory === "all" && brandAccent && "font-semibold",
          selectedCategory !== "all" && "bg-background border-border"
        )}
        style={selectedCategory === "all" && brandAccent ? { backgroundColor: brandAccent, color: contrastText(brandAccent), borderColor: brandAccent } : undefined}
        onClick={() => onSelectCategory("all")}
      >
        {tMap("category.all", { count: totalPois })}
      </Button>

      {/* "Saved" chip - only shown when user has bookmarks */}
      {savedCount > 0 && (
        <Button
          ref={selectedCategory === "saved" ? selectedRef : undefined}
          role="tab"
          aria-selected={selectedCategory === "saved"}
          aria-label={tMap("category.showSaved", { count: savedCount })}
          variant={selectedCategory === "saved" ? "default" : "outline"}
          size="sm"
          className={cn(
            "shrink-0 h-10 min-w-[72px] px-4 rounded-full",
            "inline-flex items-center gap-2",
            "text-sm font-medium whitespace-nowrap",
            selectedCategory === "saved" && !brandAccent && "font-semibold bg-foreground text-background hover:bg-foreground/90",
            selectedCategory === "saved" && brandAccent && "font-semibold",
            selectedCategory !== "saved" && "bg-background border-border"
          )}
          style={selectedCategory === "saved" && brandAccent ? { backgroundColor: brandAccent, color: contrastText(brandAccent), borderColor: brandAccent } : undefined}
          onClick={() => onSelectCategory("saved")}
        >
          <FontAwesomeIcon icon={faHeart} className="h-3.5 w-3.5 fill-current" />
          <span>{tMap("category.saved", { count: savedCount })}</span>
        </Button>
      )}

      {/* "Happening Now" chip - only shown when events are active */}
      {happeningNowCount > 0 && (
        <Button
          ref={selectedCategory === "happening-now" ? selectedRef : undefined}
          role="tab"
          aria-selected={selectedCategory === "happening-now"}
          aria-label={tMap("category.showHappeningNow", { count: happeningNowCount })}
          variant={selectedCategory === "happening-now" ? "default" : "outline"}
          size="sm"
          className={cn(
            "shrink-0 h-10 min-w-[72px] px-4 rounded-full",
            "inline-flex items-center gap-2",
            "text-sm font-medium whitespace-nowrap",
            selectedCategory === "happening-now" && "font-semibold bg-foreground text-background hover:bg-foreground/90",
            selectedCategory !== "happening-now" && "bg-background border-border",
            // Special styling - green accent when not selected
            selectedCategory !== "happening-now" && "border-green-300 text-green-700"
          )}
          onClick={() => onSelectCategory("happening-now")}
        >
          <FontAwesomeIcon icon={faBolt} className="h-3.5 w-3.5" />
          <span>{tMap("category.now", { count: happeningNowCount })}</span>
        </Button>
      )}

      {/* Category chips - sized for ~3.5 visible on 375px mobile viewport */}
      {visibleCategories.map((cat) => {
        const iconEntry = findIconByName(cat.icon);
        const isSelected = selectedCategory === cat.slug;
        const count = poiCounts[cat.slug] ?? 0;
        const dbTranslation = i18n.language !== "en" ? cat.nameTranslations?.[i18n.language] : undefined;
        const catName = dbTranslation ?? tPois(`category.${cat.slug}`, { defaultValue: cat.name });

        return (
          <Button
            key={cat.slug}
            ref={isSelected ? selectedRef : undefined}
            role="tab"
            aria-selected={isSelected}
            aria-label={tMap("category.filterBy", { name: catName, count })}
            variant={isSelected ? "default" : "outline"}
            size="sm"
            className={cn(
              "shrink-0 h-10 min-w-[90px] px-4 rounded-full",
              "inline-flex items-center gap-2",
              "text-sm font-medium whitespace-nowrap",
              isSelected && !brandAccent && "font-semibold bg-foreground text-background hover:bg-foreground/90",
              isSelected && brandAccent && "font-semibold",
              !isSelected && "bg-background border-border"
            )}
            style={isSelected && brandAccent ? { backgroundColor: brandAccent, color: contrastText(brandAccent), borderColor: brandAccent } : undefined}
            onClick={() => onSelectCategory(cat.slug)}
          >
            {iconEntry && (
              <FontAwesomeIcon icon={iconEntry.icon} className="h-3.5 w-3.5" />
            )}
            <span>{catName}</span>
            <span className="text-xs opacity-70">({count})</span>
          </Button>
        );
      })}
    </div>
  );
}
